// src/Store.js
import React, { useEffect, useRef, useState } from "react";

// TODO: replace these once your Shopify is set up
const SHOPIFY_DOMAIN = "YOUR-SHOP-NAME.myshopify.com";
const STOREFRONT_ACCESS_TOKEN = "YOUR_ACCESS_TOKEN";
const COLLECTION_ID = "YOUR_COLLECTION_ID"; // numeric/string ID from Shopify

const SDK_SRC =
  "https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js";

function Store() {
  const containerRef = useRef(null);
  const initializedRef = useRef(false); // prevents double init in Strict Mode
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // simple sanity check so we show a nice message while you're still setting up
  const hasPlaceholders =
    !SHOPIFY_DOMAIN.includes(".myshopify.com") ||
    STOREFRONT_ACCESS_TOKEN === "YOUR_ACCESS_TOKEN" ||
    COLLECTION_ID === "YOUR_COLLECTION_ID";

  useEffect(() => {
    if (initializedRef.current) return; // guard for React Strict Mode
    initializedRef.current = true;

    // If you’re still on placeholders, don’t try to load the SDK
    if (hasPlaceholders) {
      setLoading(false);
      return;
    }

    // load script (only once)
    const loadScript = () =>
      new Promise((resolve, reject) => {
        if (window.ShopifyBuy && window.ShopifyBuy.UI) return resolve();
        const existing = document.querySelector(`script[src="${SDK_SRC}"]`);
        if (existing) {
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", reject);
          return;
        }
        const script = document.createElement("script");
        script.src = SDK_SRC;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Shopify SDK"));
        document.body.appendChild(script);
      });

    const initBuyButton = async () => {
      try {
        await loadScript();

        // Build client
        const client = window.ShopifyBuy.buildClient({
          domain: SHOPIFY_DOMAIN,
          storefrontAccessToken: STOREFRONT_ACCESS_TOKEN,
        });

        // Create UI
        const ui = await window.ShopifyBuy.UI.onReady(client);

        // Render a Collection component into our container
        ui.createComponent("collection", {
          id: COLLECTION_ID,
          node: containerRef.current,
          moneyFormat: "%24%7B%7Bamount%7D%7D", // ${{amount}}
          options: {
            product: {
              buttonDestination: "modal",
              isButton: true,
              contents: {
                options: false,
              },
              styles: {
                product: { "@media (min-width: 601px)": { maxWidth: "240px" } },
                button: { borderRadius: "9999px", padding: "12px 16px" },
              },
              text: {
                button: "View",
              },
            },
            productSet: {
              styles: {
                products: { display: "grid", gap: "1.25rem", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" },
              },
            },
            modalProduct: {
              contents: {
                img: true,
                imgWithCarousel: true,
                button: true,
                buttonWithQuantity: true,
              },
            },
            cart: {
              startOpen: false,
              styles: { button: { borderRadius: "9999px" } },
              text: { total: "Subtotal", button: "Checkout" },
            },
            toggle: {
              styles: { toggle: { borderRadius: "9999px" } },
            },
          },
        });

        setLoading(false);
      } catch (e) {
        setError(e?.message || "Unable to initialize store.");
        setLoading(false);
      }
    };

    initBuyButton();

    // Cleanup: clear embeds on unmount to avoid duplicates on remounts
    return () => {
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [hasPlaceholders]);

  if (hasPlaceholders) {
    return (
      <div className="store">
        <h1>Shop</h1>
        <p>
          Your store isn’t connected yet. When you’re ready, replace:
        </p>
        <ul>
          <li><code>SHOPIFY_DOMAIN</code></li>
          <li><code>STOREFRONT_ACCESS_TOKEN</code></li>
          <li><code>COLLECTION_ID</code></li>
        </ul>
        <p>
          Then this page will automatically render your collection using Shopify’s Buy Button SDK.
        </p>
      </div>
    );
  }

  if (loading) return <div className="store"><p>Loading shop…</p></div>;
  if (error) return <div className="store"><p style={{ color: "crimson" }}>{error}</p></div>;

  return (
    <div className="store">
      <h1>Shop</h1>
      <div id="store-container" ref={containerRef} />
    </div>
  );
}

export default Store;
